import {TEmptyRecord, type Expression} from '@packages/systems/wfdl-ast';
import type {Type} from '../DataTypeDefinition';
import * as VariableMap from '../VariableMap';
import * as Variable from '../Variable';
import {type Qualified, type IconName} from '@packages/systems/wfdl-Qualified';

// 'visible' and 'hidden' behave the same from the type-checker perspective,
// but differ in what is shown in the UI: 'hidden' exports are omitted from
// the binding menu.
type Exported = 'no' | 'visible' | 'hidden';

type Implicit = 'prefilledArgs' | 'holeArg' | 'no';

// @TODO
// 1. Make opaque
// 2. Write getter
// https://app.clickup.com/t/2310520/CORE-1180
export type NormalizedEnvType = {
  dataType: Type;
  exported: Exported;
  implicit: Implicit;
  preferTypeBindUI: boolean;
};
export type EnvType = {
  dataType: Type;
  exported?: Exported;
  // Prebindable flag presumes a function value being provided to it,
  // this API is experimental and temporary during the development of
  // the Generic Bindings with Dynamo project.
  implicit?: Implicit;
  // If set to true, the binding UI provided by the TypeImplementation of the
  // underlying dataType of this EnvType will be used instead of the default
  // binding UI or a custom binding UI provided dataType of the field that this
  // value is bound to.
  preferTypeBindUI?: boolean;
};

export type SafeTypedValue = {
  envType: EnvType;
  expression: Expression;
  icon?: Qualified<IconName>;
};

export type UnsafeTypedValue = {
  envType: EnvType;
  value: unknown;
  icon?: Qualified<IconName>;
};

export type TypedValue = UnsafeTypedValue | SafeTypedValue;

export function normalizeEnvType({
  dataType,
  exported,
  implicit,
  preferTypeBindUI,
}: EnvType): NormalizedEnvType {
  return {
    dataType: dataType || TEmptyRecord,
    exported: exported || 'no',
    implicit: implicit || 'no',
    preferTypeBindUI: preferTypeBindUI ?? false,
  };
}
export type NormalizedTypedValue = {
  envType: NormalizedEnvType;
  value: unknown;
  expression: Expression;
  icon?: Qualified<IconName>;
};

export function getType(
  envTypes: VariableMap.Map<NormalizedEnvType>,
  key: Variable.VariableType
): Type | null {
  const entry = VariableMap.get<NormalizedEnvType>(envTypes, key);
  return (entry && entry.dataType) || null;
}
